import { useState, type FormEvent } from 'react';
import { formatMinutes } from '@otakotakdev/shared';
import type { TodaySnapshot } from '@otakotakdev/shared';
import {
  useToday,
  useCreateNote,
  useCreateActivity,
  useCreateGoal,
} from '@/hooks/api';
import { Badge, EmptyState, ErrorState, ListSkeleton, PageHeader } from '@/components/ui';

type CaptureTab = 'note' | 'activity' | 'goal';

function QuickCapture() {
  const [tab, setTab] = useState<CaptureTab>('note');
  const [text, setText] = useState('');
  const [minutes, setMinutes] = useState('');
  const createNote = useCreateNote();
  const createActivity = useCreateActivity();
  const createGoal = useCreateGoal();
  const [feedback, setFeedback] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const today = new Date().toISOString().slice(0, 10);
    const done = (msg: string) => {
      setFeedback(msg);
      setText('');
      setMinutes('');
      window.setTimeout(() => setFeedback(null), 3000);
    };
    if (tab === 'note') {
      createNote.mutate(
        { content: text.trim(), noteType: 'quick_note', source: 'dashboard' },
        { onSuccess: () => done('Note saved'), onError: () => setFeedback('Failed to save note') },
      );
    } else if (tab === 'activity') {
      createActivity.mutate(
        {
          title: text.trim(),
          activityDate: today,
          status: 'completed',
          durationMinutes: minutes ? Number(minutes) : null,
          source: 'dashboard',
        },
        { onSuccess: () => done('Activity logged'), onError: () => setFeedback('Failed to log activity') },
      );
    } else {
      createGoal.mutate(
        { title: text.trim(), status: 'active', priority: 'medium', source: 'dashboard' },
        { onSuccess: () => done('Goal created'), onError: () => setFeedback('Failed to create goal') },
      );
    }
  };

  const pending = createNote.isPending || createActivity.isPending || createGoal.isPending;

  return (
    <form onSubmit={submit} className="card p-4" aria-label="Quick capture">
      <div className="mb-3 flex gap-2" role="tablist" aria-label="Capture type">
        {(
          [
            ['note', 'Note'],
            ['activity', 'Activity'],
            ['goal', 'Goal'],
          ] as [CaptureTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`btn ${tab === key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className="input flex-1"
          placeholder={
            tab === 'note'
              ? 'Jot a note…'
              : tab === 'activity'
                ? 'What did you do?'
                : 'What do you want to achieve?'
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label={`${tab} text`}
        />
        {tab === 'activity' && (
          <input
            className="input w-full sm:w-28"
            type="number"
            min={0}
            placeholder="min"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            aria-label="Duration in minutes"
          />
        )}
        <button type="submit" className="btn-primary" disabled={pending || !text.trim()}>
          Add
        </button>
      </div>
      {feedback && (
        <p className="mt-2 text-sm text-success" role="status">
          {feedback}
        </p>
      )}
    </form>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}

function GoalRow({ goal }: { goal: TodaySnapshot['activeGoals'][number] }) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-white/5 py-2 last:border-0">
      <span className="truncate text-sm">{goal.title}</span>
      <span className="flex shrink-0 gap-1.5">
        <Badge tone={goal.priority === 'high' ? 'danger' : goal.priority === 'medium' ? 'warning' : 'default'}>
          {goal.priority}
        </Badge>
        {goal.targetDate && <Badge>{goal.targetDate}</Badge>}
      </span>
    </li>
  );
}

function ActivityRow({
  activity,
  showStatus = true,
}: {
  activity: TodaySnapshot['plannedActivities'][number];
  showStatus?: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-white/5 py-2 last:border-0">
      <span className="truncate text-sm">{activity.title}</span>
      <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted">
        {activity.durationMinutes != null && <span>{formatMinutes(activity.durationMinutes)}</span>}
        {showStatus && (
          <Badge tone={activity.status === 'completed' ? 'success' : 'default'}>{activity.status}</Badge>
        )}
      </span>
    </li>
  );
}

export function TodayPage() {
  const { data, isLoading, isError, error, refetch } = useToday();

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Today" />
        <ListSkeleton rows={5} />
      </div>
    );
  }
  if (isError) {
    return (
      <div>
        <PageHeader title="Today" />
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load today'}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }
  if (!data) return null;

  const s = data;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={`${s.greeting}`}
        subtitle={`${s.localDate} · ${s.timezone}`}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Active goals" value={String(s.progress.goalsActive)} hint={`${s.progress.goalsDueToday} due today`} />
        <StatCard label="Activities done" value={`${s.progress.activitiesCompleted}/${s.progress.activitiesPlanned}`} />
        <StatCard label="Time logged" value={formatMinutes(s.totalMinutesLogged)} />
        <StatCard label="Overdue" value={String(s.overdueGoals.length)} />
      </div>

      <div className="mb-6">
        <QuickCapture />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="card p-4" aria-labelledby="due-today">
          <h2 id="due-today" className="mb-2 text-sm font-semibold text-muted">
            Goals due today
          </h2>
          {s.goalsDueToday.length === 0 ? (
            <p className="text-sm text-muted">Nothing due today.</p>
          ) : (
            <ul>
              {s.goalsDueToday.map((g) => (
                <GoalRow key={g.id} goal={g} />
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="overdue">
          <h2 id="overdue" className="mb-2 text-sm font-semibold text-muted">
            Overdue goals
          </h2>
          {s.overdueGoals.length === 0 ? (
            <p className="text-sm text-muted">Nothing overdue — nice.</p>
          ) : (
            <ul>
              {s.overdueGoals.map((g) => (
                <GoalRow key={g.id} goal={g} />
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="planned">
          <h2 id="planned" className="mb-2 text-sm font-semibold text-muted">
            Planned today
          </h2>
          {s.plannedActivities.length === 0 ? (
            <p className="text-sm text-muted">No planned activities.</p>
          ) : (
            <ul>
              {s.plannedActivities.map((a) => (
                <ActivityRow key={a.id} activity={a} />
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4" aria-labelledby="completed">
          <h2 id="completed" className="mb-2 text-sm font-semibold text-muted">
            Completed today
          </h2>
          {s.completedActivities.length === 0 ? (
            <p className="text-sm text-muted">Nothing completed yet.</p>
          ) : (
            <ul>
              {s.completedActivities.map((a) => (
                <ActivityRow key={a.id} activity={a} />
              ))}
            </ul>
          )}
        </section>
      </div>

      {s.unfinishedCarryover.length > 0 && (
        <section className="card mt-4 p-4" aria-labelledby="carryover">
          <h2 id="carryover" className="mb-2 text-sm font-semibold text-muted">
            Carried over from before
          </h2>
          <ul>
            {s.unfinishedCarryover.map((a) => (
              <ActivityRow key={a.id} activity={a} />
            ))}
          </ul>
        </section>
      )}

      <section className="card mt-4 p-4" aria-labelledby="timeline">
        <h2 id="timeline" className="mb-2 text-sm font-semibold text-muted">
          Recent timeline
        </h2>
        {s.recentTimeline.length === 0 ? (
          <EmptyState title="Nothing here yet" hint="Create a goal, activity, or note to get started." />
        ) : (
          <ul>
            {s.recentTimeline.map((entry) => (
              <li key={`${entry.kind}-${entry.id}`} className="flex items-center gap-3 border-b border-white/5 py-2 text-sm last:border-0">
                <Badge tone={entry.kind === 'goal' ? 'accent' : entry.kind === 'note' ? 'warning' : 'success'}>
                  {entry.kind}
                </Badge>
                <span className="truncate">{entry.title}</span>
                <span className="ml-auto shrink-0 text-xs text-muted">
                  {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
